class ContactEnquiry extends HTMLElement {
  #controller = new AbortController();

  get #form() {
    return this.querySelector('.contact-enquiry__form');
  }

  get #dropdowns() {
    return this.querySelectorAll('[data-dropdown]');
  }

  connectedCallback() {
    this.#initDropdowns();
    this.#initFormValidation();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #initDropdowns() {
    const { signal } = this.#controller;

    this.#dropdowns.forEach((dropdown) => {
      const trigger = dropdown.querySelector('.contact-enquiry__dropdown-trigger');
      const menu = dropdown.querySelector('.contact-enquiry__dropdown-menu');
      const label = dropdown.querySelector('[data-dropdown-label]');
      const hiddenInput = dropdown.closest('.contact-enquiry__field').querySelector('input[type="hidden"]');

      const openMenu = () => {
        trigger.setAttribute('aria-expanded', 'true');
        menu.hidden = false;
        menu.style.display = 'flex';
        dropdown.classList.add('contact-enquiry__dropdown--open');
      };

      const closeMenu = () => {
        trigger.setAttribute('aria-expanded', 'false');
        menu.hidden = true;
        menu.style.display = 'none';
        dropdown.classList.remove('contact-enquiry__dropdown--open');
      };

      trigger.addEventListener(
        'click',
        (e) => {
          e.preventDefault();
          const isOpen = trigger.getAttribute('aria-expanded') === 'true';
          if (isOpen) {
            closeMenu();
          } else {
            openMenu();
          }
        },
        { signal },
      );

      menu.querySelectorAll('.contact-enquiry__dropdown-option').forEach((option) => {
        option.addEventListener(
          'click',
          (e) => {
            e.preventDefault();
            label.textContent = option.dataset.value;
            label.classList.add('contact-enquiry__dropdown-label--selected');
            hiddenInput.value = option.dataset.value;
            closeMenu();
            dropdown.closest('.contact-enquiry__field').classList.remove('contact-enquiry__field--has-error');
          },
          { signal },
        );
      });

      document.addEventListener(
        'click',
        (e) => {
          if (!dropdown.contains(e.target)) {
            closeMenu();
          }
        },
        { signal },
      );
    });
  }

  #initFormValidation() {
    const form = this.#form;
    if (!form) return;

    const { signal } = this.#controller;

    form.addEventListener(
      'submit',
      (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();

        let hasError = false;

        form.querySelectorAll('.contact-enquiry__field').forEach((field) => {
          field.classList.remove('contact-enquiry__field--has-error');
        });

        const nameInput = form.querySelector('input[name="contact[name]"]');
        if (nameInput && !nameInput.value.trim()) {
          nameInput.closest('.contact-enquiry__field')?.classList.add('contact-enquiry__field--has-error');
          hasError = true;
        }

        const emailInput = form.querySelector('input[name="contact[email]"]');
        if (emailInput && (!emailInput.value.trim() || !this.#isValidEmail(emailInput.value.trim()))) {
          emailInput.closest('.contact-enquiry__field')?.classList.add('contact-enquiry__field--has-error');
          hasError = true;
        }

        const phoneInput = form.querySelector('input[name="contact[phone]"]');
        if (phoneInput && !phoneInput.value.trim()) {
          phoneInput.closest('.contact-enquiry__field')?.classList.add('contact-enquiry__field--has-error');
          hasError = true;
        }

        const subjectInput = form.querySelector('input[name="contact[subject]"]');
        if (subjectInput && !subjectInput.value) {
          subjectInput.closest('.contact-enquiry__field')?.classList.add('contact-enquiry__field--has-error');
          hasError = true;
        }

        const messageInput = form.querySelector('textarea[name="contact[body]"]');
        if (messageInput && !messageInput.value.trim()) {
          messageInput.closest('.contact-enquiry__field')?.classList.add('contact-enquiry__field--has-error');
          hasError = true;
        }

        const consent = form.querySelector('.contact-enquiry__checkbox');
        if (consent && !consent.checked) {
          consent.closest('.contact-enquiry__field')?.classList.add('contact-enquiry__field--has-error');
          hasError = true;
        }

        if (hasError) {
          const firstError = form.querySelector('.contact-enquiry__field--has-error');
          if (firstError) firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }

        form.submit();
      },
      { signal },
    );

    form.querySelectorAll('.contact-enquiry__input').forEach((input) => {
      input.addEventListener(
        'input',
        () => {
          const field = input.closest('.contact-enquiry__field');
          if (field) field.classList.remove('contact-enquiry__field--has-error');
        },
        { signal },
      );
    });
  }

  #isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }
}

if (!customElements.get('contact-enquiry')) {
  customElements.define('contact-enquiry', ContactEnquiry);
}
